import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { OcrCapture, type OcrResult } from '../components/OcrCapture.js';
import { RiskWidget, type RiskFeatures } from '../components/RiskWidget.js';
import { friendly_error } from '../lib/friendly-error.js';

export function SubscribersNewPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lang = current_language();

  const [name_ta, set_name_ta] = useState('');
  const [name_en, set_name_en] = useState('');
  const [phone, set_phone] = useState('+91');
  const [aadhaar, set_aadhaar] = useState('');
  const [pan, set_pan] = useState('');
  const [occupation, set_occupation] = useState('');
  const [has_guarantor, set_has_guarantor] = useState<boolean | null>(null);
  const [town_ta, set_town_ta] = useState('');
  const [town_en, set_town_en] = useState('');
  const [risk, set_risk] = useState<'low' | 'medium' | 'high'>('low');
  const [dob_from_ocr, set_dob_from_ocr] = useState<string | null>(null);
  const [ocr_summary, set_ocr_summary] = useState<string | null>(null);
  const [ocr_confidence, set_ocr_confidence] = useState<number | null>(null);

  const apply_ocr = (r: OcrResult): void => {
    set_ocr_confidence(r.confidence);
    if (r.kind === 'aadhaar') {
      // Fill empty fields; never overwrite operator edits
      if (!name_en) set_name_en(r.name_en);
      if (!name_ta) set_name_ta(r.name_ta);
      if (!aadhaar) set_aadhaar(r.aadhaar_last4);
      if (!town_en && r.district) set_town_en(r.district);
      set_dob_from_ocr(r.dob);
      set_ocr_summary(
        `Aadhaar · ${r.name_en} · last-4 ${r.aadhaar_last4} · ${r.district}, ${r.pincode}`,
      );
    } else if (r.kind === 'pan') {
      if (!name_en) set_name_en(r.name_en);
      if (!dob_from_ocr) set_dob_from_ocr(r.dob);
      if (!pan) set_pan(r.pan);
      set_ocr_summary(`PAN · ${r.pan} · ${r.name_en}`);
    } else {
      set_ocr_summary(`Cheque · ${r.bank_name} · ${r.ifsc} · acct ${r.account_number}`);
    }
  };

  const m = useMutation({
    mutationFn: async () =>
      orpc.subscribers.create({
        name_ta,
        name_en,
        phone,
        ...(aadhaar ? { aadhaar_last4: aadhaar } : {}),
        ...(pan ? { pan } : {}),
        ...(occupation ? { occupation } : {}),
        ...(town_ta ? { town_ta } : {}),
        ...(town_en ? { town_en } : {}),
        risk_classification: risk,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members'] });
      navigate('/subscribers');
    },
  });

  // Build feature payload for the AI risk widget. Pure derivation — no PII.
  const risk_features: RiskFeatures = {
    ...(dob_from_ocr ? { age_years: estimate_age(dob_from_ocr) } : {}),
    ...(occupation ? { occupation } : {}),
    ...(town_en ? { district: town_en } : {}),
    has_aadhaar: aadhaar.length === 4,
    has_pan: /^[A-Z]{5}\d{4}[A-Z]$/.test(pan),
    ...(has_guarantor !== null ? { has_guarantor } : {}),
  };

  const label_cls = `block mb-1 text-sm ${lang === 'ta' ? 'font-tamil' : 'font-sans'}`;
  const input_cls =
    'w-full px-3 py-2 border border-line rounded bg-bg-elev focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div>
      <h1 className="text-2xl font-tamil text-primary mb-6">{t('subscribers.create_title')}</h1>

      {/* OCR capture strip — operator snaps Aadhaar/PAN to auto-fill below */}
      <div className="bg-info-soft border border-info/30 rounded-lg p-4 mb-4">
        <div className="text-xs font-medium text-info mb-2">
          {t('ocr.title', 'Scan KYC docs to auto-fill')}
        </div>
        <div className="flex flex-wrap gap-2">
          <OcrCapture kind="aadhaar" label={t('ocr.aadhaar', 'Scan Aadhaar')} on_result={apply_ocr} />
          <OcrCapture kind="pan" label={t('ocr.pan', 'Scan PAN')} on_result={apply_ocr} />
        </div>
        {ocr_summary && (
          <div className="text-[11px] text-info mt-2 break-all">
            ✓ {ocr_summary}
            {typeof ocr_confidence === 'number' && (
              <span className="ml-2 opacity-70">
                ({Math.round(ocr_confidence * 100)}% confidence)
              </span>
            )}
          </div>
        )}
      </div>

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          m.mutate();
        }}
        className="bg-surface border border-line rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <label>
          <span className={label_cls}>{t('subscribers.fields.name_ta')}</span>
          <input
            className={`${input_cls} font-tamil`}
            value={name_ta}
            onChange={(e) => set_name_ta(e.target.value)}
            placeholder="முருகேஸ்வரி கணேசன்"
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.name_en')}</span>
          <input
            className={input_cls}
            value={name_en}
            onChange={(e) => set_name_en(e.target.value)}
            placeholder="Murugeswari Ganesan"
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.phone')}</span>
          <input
            type="tel"
            className={`${input_cls} font-mono`}
            value={phone}
            onChange={(e) => set_phone(e.target.value)}
            placeholder="+91 9876543210"
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.aadhaar_last4')}</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            className={`${input_cls} font-mono`}
            value={aadhaar}
            onChange={(e) => set_aadhaar(e.target.value)}
            placeholder="1234"
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.pan', 'PAN')}</span>
          <input
            type="text"
            pattern="[A-Z]{5}[0-9]{4}[A-Z]"
            maxLength={10}
            className={`${input_cls} font-mono uppercase`}
            value={pan}
            onChange={(e) => set_pan(e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.occupation', 'Occupation')}</span>
          <input
            type="text"
            maxLength={80}
            className={input_cls}
            value={occupation}
            onChange={(e) => set_occupation(e.target.value)}
            placeholder="Shop owner"
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.guarantor', 'Guarantor')}</span>
          <div className="flex gap-2">
            {(
              [
                ['yes', true],
                ['no', false],
                ['—', null],
              ] as Array<['yes' | 'no' | '—', boolean | null]>
            ).map(([label, val]) => (
              <button
                type="button"
                key={label}
                onClick={() => set_has_guarantor(val)}
                className={`px-3 py-1.5 rounded text-xs font-medium border ${
                  has_guarantor === val
                    ? 'bg-primary-soft border-primary text-primary-ink'
                    : 'bg-bg-elev border-line text-ink/60'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.town_ta')}</span>
          <input
            className={`${input_cls} font-tamil`}
            value={town_ta}
            onChange={(e) => set_town_ta(e.target.value)}
            placeholder="சிவகாசி"
          />
        </label>
        <label>
          <span className={label_cls}>{t('subscribers.fields.town_en')}</span>
          <input
            className={input_cls}
            value={town_en}
            onChange={(e) => set_town_en(e.target.value)}
            placeholder="Sivakasi"
          />
        </label>
        <div className="md:col-span-2">
          <RiskWidget features={risk_features} on_apply_class={set_risk} />
        </div>
        <label className="md:col-span-2">
          <span className={label_cls}>Risk</span>
          <div className="flex gap-2">
            {(['low', 'medium', 'high'] as const).map((r) => (
              <button
                type="button"
                key={r}
                onClick={() => set_risk(r)}
                className={`px-3 py-1.5 rounded text-xs font-medium border ${
                  risk === r
                    ? r === 'high'
                      ? 'bg-bad-soft border-bad text-bad'
                      : r === 'medium'
                      ? 'bg-warn-soft border-warn text-warn'
                      : 'bg-good-soft border-good text-good'
                    : 'bg-bg-elev border-line text-ink/60'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </label>
        {m.error && (
          <div className="md:col-span-2 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
            {friendly_error(m.error)}
          </div>
        )}
        <div className="md:col-span-2 flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={() => navigate('/subscribers')}
            className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={m.isPending}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {t('actions.create')}
          </button>
        </div>
      </form>
    </div>
  );
}

function estimate_age(dob: string): number | undefined {
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  const birth = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const now = new Date();
  const years = (now.getTime() - birth.getTime()) / (365.25 * 86_400_000);
  if (!Number.isFinite(years) || years < 18 || years > 100) return undefined;
  return Math.floor(years);
}
